import * as React from 'react';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Alert from 'antd/lib/alert';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import { FilterFilled } from '@ant-design/icons';
import { type SummaryViewVersionFilterConfig } from '@/types/summaryView';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';

const { Option } = Select;

export const versionsFilterIcon = (filtered: boolean) => (
    <FilterFilled style={{ color: filtered ? runwayColors.primary : antGrey.grey6 }} />
);

export const isVersionsFiltered = (config: SummaryViewVersionFilterConfig): boolean => {
    const sortedDisplayed = [...config.displayedVersions].sort().join('\0');
    const sortedAvailable = [...config.availableVersions].sort().join('\0');
    return sortedDisplayed !== sortedAvailable;
};

interface VersionsFilterContentProps {
    isVisible: boolean;
    currentVersions: string[];
    availableVersions: string[];
    defaultVersions?: string[];
    isSubmitting: boolean;
    onApply: (versions: string[]) => void;
    onClose: () => void;
}

const VersionsFilterContent: React.FC<VersionsFilterContentProps> = (props) => {
    const [form] = Form.useForm();
    const versions = Form.useWatch('versions', form) || [];
    const hasNoVersions = versions.length === 0;

    const prevIsVisible = React.useRef(false);

    React.useEffect(() => {
        if (props.isVisible && !prevIsVisible.current) {
            form.setFieldsValue({
                versions: props.currentVersions,
            });
        }
        prevIsVisible.current = props.isVisible;
    }, [props.isVisible, props.currentVersions, form]);

    const clearFilters = () => {
        const resetVersions = props.defaultVersions?.length ? props.defaultVersions : props.availableVersions;
        form.setFieldsValue({ versions: resetVersions });
    };

    const applyFilters = (values: { versions?: string[] }) => {
        const next = values.versions || [];
        if (next.length === 0) {
            return;
        }
        props.onApply(next);
        props.onClose();
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div
                style={{
                    padding: '18px 16px',
                    maxHeight: 450,
                }}
            >
                <Form
                    form={form}
                    initialValues={{
                        versions: props.currentVersions,
                    }}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                >
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Flex vertical>
                            <Flex style={{ paddingBottom: 8 }} justify="space-between">
                                <Text style={{ fontSize: 14, color: antGrey.grey8 }}> Versions </Text>
                                <Button
                                    type="link"
                                    onClick={() => {
                                        const all = props.availableVersions;
                                        form.setFieldsValue({ versions: all });
                                    }}
                                    style={{ padding: 0, height: 'fit-content' }}
                                >
                                    Select all
                                </Button>
                            </Flex>
                            <Form.Item name="versions" noStyle>
                                <Select
                                    mode="multiple"
                                    placeholder="Select versions"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={'responsive'}
                                    style={{ width: 400 }}
                                >
                                    {props.availableVersions.map((version) => (
                                        <Option key={version} value={version}>
                                            {version}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Flex>
                    </Form.Item>
                </Form>
            </div>
            {hasNoVersions && (
                <Alert
                    type="warning"
                    showIcon
                    message="Please select at least one version"
                    style={{ margin: '0px 16px 12px 16px' }}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0px 16px 12px 16px',
                }}
            >
                <Button size="small" onClick={clearFilters} disabled={props.isSubmitting}>
                    Clear
                </Button>
                <Button
                    size="small"
                    onClick={form.submit}
                    type="primary"
                    loading={props.isSubmitting}
                    disabled={hasNoVersions}
                >
                    Apply
                </Button>
            </div>
        </div>
    );
};

export const VersionColumnFilter: React.FC<{
    config: SummaryViewVersionFilterConfig;
    close: () => void;
    visible: boolean;
}> = ({ config, close, visible }) => (
    <VersionsFilterContent
        isVisible={visible}
        currentVersions={config.displayedVersions}
        availableVersions={config.availableVersions}
        defaultVersions={config.defaultVersions}
        isSubmitting={config.isSubmitting}
        onApply={config.onApply}
        onClose={close}
    />
);

export const CompactVersionRowFilter: React.FC<{ config: SummaryViewVersionFilterConfig }> = ({ config }) => {
    const [open, setOpen] = React.useState(false);

    return (
        <Popover
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomLeft"
            destroyTooltipOnHide
            overlayInnerStyle={{ padding: 0 }}
            content={
                <VersionsFilterContent
                    isVisible={open}
                    currentVersions={config.displayedVersions}
                    availableVersions={config.availableVersions}
                    defaultVersions={config.defaultVersions}
                    isSubmitting={config.isSubmitting}
                    onApply={config.onApply}
                    onClose={() => setOpen(false)}
                />
            }
        >
            <Space size={6} style={{ cursor: 'pointer', justifyContent: 'space-between', width: '100%' }}>
                <Text style={{ fontSize: 12, color: antGrey.grey7 }}>Version</Text>
                {versionsFilterIcon(isVersionsFiltered(config))}
            </Space>
        </Popover>
    );
};
